// utils/endpoints.js
const API_BASE =
    process.env.REACT_APP_API_URL ||
    "http://localhost:8000/api";

// helper
const withId = (base) => ({
    BASE: base,
    BY_ID: (id) => `${base}/${id}`
});

export const ENDPOINTS = {

    // =====================
    // AUTH
    // =====================
    AUTH: {
        LOGIN: `${API_BASE}/auth/login`,
        REGISTER: `${API_BASE}/auth/register`,
        LOGOUT: `${API_BASE}/auth/logout`,
        REFRESH: `${API_BASE}/auth/refresh`,
        ME: `${API_BASE}/auth/me`
    },

    // =====================
    // USERS + RBAC
    // =====================
    USERS: withId(`${API_BASE}/users`),

    ROLES: {
        ...withId(`${API_BASE}/roles`),
        ASSIGN: `${API_BASE}/roles/assign`
    },

    PERMISSIONS: withId(`${API_BASE}/permissions`),

    TEACHER_PROFILES: withId(`${API_BASE}/teacher-profiles`),
    STUDENT_PROFILES: withId(`${API_BASE}/student-profiles`),
    STAFF_PROFILES: withId(`${API_BASE}/staff-profiles`),

    // =====================
    // STRUCTURE ACADÉMIQUE
    // =====================
    FACULTIES: withId(`${API_BASE}/faculties`),
    DEPARTMENTS: withId(`${API_BASE}/departments`),
    ACADEMIC_YEARS: {
        ...withId(`${API_BASE}/academic-years`),
        CURRENT: `${API_BASE}/academic-years/current`,
        SET_CURRENT: (id) => `${API_BASE}/academic-years/${id}/set-current`
    },
    SEMESTERS: withId(`${API_BASE}/semesters`),
    LEVELS: withId(`${API_BASE}/levels`),
    CLASSES: {
        ...withId(`${API_BASE}/classes`),
        MODULES: (classId) => `${API_BASE}/classes/${classId}/modules`,
    },
    GROUPS: withId(`${API_BASE}/student-groups`),
    SUBJECTS: withId(`${API_BASE}/subjects`),
    MODULES: withId(`${API_BASE}/modules`),
    ENROLLMENTS: withId(`${API_BASE}/enrollments`),

    // =====================
    // BANQUE DE QUESTIONS
    // =====================
    QUESTIONS: {
        ...withId(`${API_BASE}/questions`),
        ANSWERS: (questionId) => `${API_BASE}/questions/${questionId}/answers`,
        ANSWER_BY_ID: (questionId, answerId) => `${API_BASE}/questions/${questionId}/answers/${answerId}`,
        IMAGE: (id) => `${API_BASE}/questions/${id}/image`
    },
    QUESTION_CATEGORIES: withId(`${API_BASE}/question-categories`),
    QUESTION_DIFFICULTIES: withId(`${API_BASE}/question-difficulties`),
    QUESTION_TYPES: withId(`${API_BASE}/question-types`),

    // =====================
    // EXAMENS
    // =====================
    EXAMS: {
        ...withId(`${API_BASE}/exams`),
        REQUEST_VALIDATION: (id) => `${API_BASE}/exams/${id}/request-validation`,
        VALIDATE: (id) => `${API_BASE}/exams/${id}/validate`,
        REOPEN: (id) => `${API_BASE}/exams/${id}/reopen`,
        PUBLISH: (id) => `${API_BASE}/exams/${id}/publish`,
        ARCHIVE: (id) => `${API_BASE}/exams/${id}/archive`,
        REJECT: (id) => `${API_BASE}/exams/${id}/reject`,
        QUESTIONS: (examId) => `${API_BASE}/exams/${examId}/questions`,
        QUESTION_BY_ID: (examId, examQuestionId) => `${API_BASE}/exams/${examId}/questions/${examQuestionId}`,
        NEUTRALIZE_QUESTION: (examId, examQuestionId) => `${API_BASE}/exams/${examId}/questions/${examQuestionId}/neutralize`
    },
    EXAM_SESSIONS: {
        ...withId(`${API_BASE}/exam-sessions`),
        OPEN: (id) => `${API_BASE}/exam-sessions/${id}/open`,
        CLOSE: (id) => `${API_BASE}/exam-sessions/${id}/close`,
        CANCEL: (id) => `${API_BASE}/exam-sessions/${id}/cancel`,
        ROOMS: (id) => `${API_BASE}/exam-sessions/${id}/rooms`,
        UNASSIGN_ROOM: (id, roomId) => `${API_BASE}/exam-sessions/${id}/rooms/${roomId}`,
        ATTENDANCE_SHEET: (id) => `${API_BASE}/exam-sessions/${id}/attendance-sheet`
    },

    // =====================
    // SALLES / TABLETTES / INCIDENTS
    // =====================
    ROOMS: { ...withId(`${API_BASE}/rooms`) },
    TABLETS: { ...withId(`${API_BASE}/tablets`) },
    EXAM_INCIDENTS: { ...withId(`${API_BASE}/exam-incidents`) },

    // =====================
    // CAS CLINIQUES / VALEURS DE RÉFÉRENCE
    // =====================
    CLINICAL_CASES: { ...withId(`${API_BASE}/clinical-cases`) },
    REFERENCE_VALUES: { ...withId(`${API_BASE}/reference-values`) },

    // =====================
    // CORRECTION DE NOTE (double validation)
    // =====================
    GRADE_CORRECTION_REQUESTS: {
        ...withId(`${API_BASE}/grade-correction-requests`),
        APPROVE: (id) => `${API_BASE}/grade-correction-requests/${id}/approve`,
        REJECT: (id) => `${API_BASE}/grade-correction-requests/${id}/reject`
    },

    // =====================
    // PASSATION ÉTUDIANTE
    // =====================
    ATTEMPTS: {
        ...withId(`${API_BASE}/student-attempts`),

        START: `${API_BASE}/student-attempts/start`,

        SUBMIT: (id) =>
            `${API_BASE}/student-attempts/${id}/submit`,

        CANCEL: (id) =>
            `${API_BASE}/student-attempts/${id}/cancel`,

        ANSWERS: (attemptId) =>
            `${API_BASE}/student-attempts/${attemptId}/answers`
    },


    // =====================
    // RÉSULTATS
    // =====================
    RESULTS: {
        ...withId(`${API_BASE}/exam-results`),

        BY_ATTEMPT: (attemptId) =>
            `${API_BASE}/exam-results/by-attempt/${attemptId}`,

        RECOMPUTE: (attemptId) =>
            `${API_BASE}/exam-results/by-attempt/${attemptId}/recompute`,

        PUBLISH: (id) => `${API_BASE}/exam-results/${id}/publish`,
        PUBLISH_BY_EXAM: (examId) => `${API_BASE}/exam-results/publish-by-exam/${examId}`,
        ANNUAL_TRANSCRIPT: (studentId) => `${API_BASE}/exam-results/annual-transcript/${studentId}`,
        ANNUAL_TRANSCRIPT_DATA: (studentId) => `${API_BASE}/exam-results/annual-transcript/${studentId}/data`,
        TRANSCRIPT: (studentId) => `${API_BASE}/exam-results/transcript/${studentId}`,
        TRANSCRIPT_DATA: (studentId) => `${API_BASE}/exam-results/transcript/${studentId}/data`
    },

    // =====================
    // IMPORT / EXPORT GÉNÉRIQUE
    // =====================
    IMPORT_EXPORT: {
        ENTITIES: `${API_BASE}/import-export/entities`,
        TEMPLATE: (entity) => `${API_BASE}/import-export/entities/${entity}/template`,
        IMPORT: (entity) => `${API_BASE}/import-export/entities/${entity}/import`,
        EXPORT: (entity) => `${API_BASE}/import-export/entities/${entity}/export`
    },

    // =====================
    // TRANSVERSE (à venir : bloc back-end en cours)
    // =====================
    STATISTICS: {
        OVERVIEW: `${API_BASE}/statistics/overview`,
        TIMELINE: `${API_BASE}/statistics/timeline`,
        SCORE_DISTRIBUTION: `${API_BASE}/statistics/score-distribution`,
        BY_SUBJECT: `${API_BASE}/statistics/by-subject`,
        FILTER_OPTIONS: `${API_BASE}/statistics/filter-options`,
        ME: `${API_BASE}/statistics/me`,
        BREAKDOWN: `${API_BASE}/statistics/breakdown`,
        MODULE_AVERAGES: `${API_BASE}/statistics/module-averages`,
        EXAM: (examId) => `${API_BASE}/statistics/exams/${examId}`,
        ACADEMIC_YEAR: (yearId) => `${API_BASE}/statistics/academic-years/${yearId}`,
        YEAR_OUTCOMES: (yearId) => `${API_BASE}/statistics/academic-years/${yearId}/outcomes`,
        YEAR_OUTCOME: (yearId, studentId) => `${API_BASE}/statistics/academic-years/${yearId}/outcomes/${studentId}`
    },
    NOTIFICATIONS: {
        ...withId(`${API_BASE}/notifications`),
        MINE: `${API_BASE}/notifications/mine`,
        UNREAD_COUNT: `${API_BASE}/notifications/unread-count`,
        MARK_READ: (id) => `${API_BASE}/notifications/${id}/read`,
        MARK_ALL_READ: `${API_BASE}/notifications/read-all`
    },
    AUDIT_LOGS: withId(`${API_BASE}/audit-logs`),
    SETTINGS: {
        BASE: `${API_BASE}/settings`,
        BY_KEY: (key) => `${API_BASE}/settings/${key}`
    }
};